/**
 * ADR 180 · F2 — El interruptor de región.
 *
 * Es un filtro GLOBAL, y de ahí sale casi todo su comportamiento:
 *  1. Elegir región NO te saca de donde estás. En una colección se re-filtra
 *     esa misma colección; en cualquier otra pantalla solo se recuerda la
 *     preferencia y se actualiza el chip.
 *  2. El programa de expansión es un modal sobre la página, no otra pantalla.
 *  3. Se recuerda la elección y se mide el uso con el `vaultEvento` del tema.
 *
 * Lo que NO hace, a propósito: aplicar la región solo. El default es nacional
 * (ver el comentario del snippet). Recordar ≠ imponer: si el comprador está
 * viendo el país entero, se queda viendo el país entero.
 *
 * `localStorage` va envuelto en try/catch porque en modo privado no falla al
 * leer — TIRA al construirse el acceso. Sin el catch se cae el módulo entero
 * (mismo cuidado que documenta vault-referencia.js).
 */
(function () {
  var LLAVE = 'vault_region';

  function leer() {
    try {
      return localStorage.getItem(LLAVE) || '';
    } catch (e) {
      return '';
    }
  }

  function guardar(estado) {
    try {
      if (estado) localStorage.setItem(LLAVE, estado);
      else localStorage.removeItem(LLAVE);
    } catch (e) {
      /* Cuota llena o storage bloqueado: seguimos sin memoria, nunca rotos. */
    }
  }

  function medir(evento, destino) {
    if (typeof window.vaultEvento === 'function') window.vaultEvento(evento, destino);
  }

  /**
   * La región que el comprador PIDIÓ.
   *
   * ─────────────────────────────────────────────────────────────────────
   * CORRECCIÓN (ADR 189 §4). Aquí decía que `location.pathname` conservaba el
   * segmento del tag y que por eso bastaba con leerlo. **Es falso justo en el
   * caso para el que se escribió.** Shopify no ignora en silencio un tag que
   * ningún producto tiene: responde **301** y el redirect reescribe la URL sin
   * ese segmento (verificado el 25-ago: Aguascalientes y Jalisco → 301,
   * CDMX → 200). Para cuando corre este código, `location.pathname` ya es el
   * de la colección pelona, así que devolvía vacío y `avisarRegionSinCatalogo()`
   * no corría nunca: los tres avisos de ADR 181 llevaban meses `hidden` en
   * producción.
   *
   * Lo que sí sobrevive es lo que se captura ANTES de navegar, en el clic:
   *   1. `sessionStorage` — inmune a cualquier redirect y muere con la pestaña.
   *      Es la fuente principal.
   *   2. `?region=` en el href — por si el enlace se comparte o se recarga en
   *      otra pestaña, donde no hay sessionStorage que valga. Secundaria porque
   *      no está garantizado que el 301 conserve el query string.
   *   3. El slug de la URL — sigue valiendo cuando el tag SÍ existe (no hay
   *      301) o cuando se llega por un enlace externo a una región con
   *      catálogo.
   * ─────────────────────────────────────────────────────────────────────
   */
  var LLAVE_PEDIDA = 'vault_region_pedida';

  /** Se llama en el clic, antes de que el navegador se mueva. */
  function recordarPedida(estado) {
    try {
      if (estado) sessionStorage.setItem(LLAVE_PEDIDA, estado);
      else sessionStorage.removeItem(LLAVE_PEDIDA);
    } catch (e) {
      /* Igual que localStorage: en modo privado TIRA al construirse el acceso.
         Sin memoria seguimos —queda el `?region=`—, nunca rotos. */
    }
  }

  function pedidaGuardada() {
    try {
      return sessionStorage.getItem(LLAVE_PEDIDA) || '';
    } catch (e) {
      return '';
    }
  }

  /** El estado que viaja en `?region=`, si el redirect lo dejó pasar. */
  function pedidaEnQuery() {
    var m = /[?&]region=([^&]*)/.exec(window.location.search);
    if (!m) return '';
    try {
      return decodeURIComponent(m[1].replace(/\+/g, ' ')).trim();
    } catch (e) {
      return '';
    }
  }

  /**
   * Escribe `?region={Estado}` en el enlace que se acaba de tocar, sin duplicar
   * el parámetro si ya estaba. Es la copia compartible de la intención: el
   * `sessionStorage` no viaja en un link pegado en WhatsApp.
   */
  function marcarDestino(enlace, estado) {
    var href = enlace.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return;
    var partes = href.split('#');
    var base = partes[0].replace(/([?&])region=[^&]*&?/, '$1').replace(/[?&]$/, '');
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    enlace.setAttribute(
      'href',
      base + sep + 'region=' + encodeURIComponent(estado) + (partes[1] ? '#' + partes[1] : '')
    );
  }

  /** Quita `?region=` de la barra de direcciones sin recargar. */
  function limpiarQuery() {
    if (!window.history || !window.history.replaceState) return;
    if (!pedidaEnQuery()) return;
    var busca = window.location.search.replace(/([?&])region=[^&]*&?/, '$1').replace(/[?&]$/, '');
    window.history.replaceState({}, '', window.location.pathname + busca + window.location.hash);
  }

  function regionPedida() {
    var query = pedidaEnQuery();
    if (query) return query;

    var recordada = pedidaGuardada();
    if (recordada) return recordada;

    /* El mapa slug→estado se arma de los propios enlaces del picker, que Liquid
       generó con `| handleize`. Así no hay una segunda copia que se
       desincronice ni hay que adivinar cómo Shopify trata los acentos. */
    var mapa = {};
    var opciones = document.querySelectorAll('.vault-region__opcion[data-estado]');
    for (var i = 0; i < opciones.length; i++) {
      var partes = (opciones[i].getAttribute('href') || '').split('/');
      var slug = (partes[partes.length - 1] || '').split('?')[0];
      if (slug) mapa[slug] = opciones[i].getAttribute('data-estado');
    }
    var segmentos = window.location.pathname.split('/').filter(Boolean);
    var ultimo = segmentos[segmentos.length - 1] || '';
    return mapa[ultimo] || '';
  }

  /**
   * En qué nivel está cada región (ADR 181). Sin esto el tema solo sabe "hay o
   * no hay cartas", y confunde dos situaciones opuestas: la región donde falta
   * inventario (ya hay dónde depositar) y aquella donde no hemos llegado.
   *
   * Caché de un día con la misma forma que vault-referencia.js: la llave lleva
   * la fecha, así caduca sola sin TTL que invalidar. Si el endpoint falla se
   * sigue sin niveles — nunca se rompe el catálogo por no poder etiquetarlo.
   *
   * Se guarda la RESPUESTA COMPLETA, no solo `regiones` (ADR 189 §5): la ficha
   * de producto lee de aquí el calendario de la valija, que viaja al lado del
   * mapa de regiones. Una sola petición para todo el tema.
   */
  var ESTADO_URL = 'https://cards.the-vault.mx/api/regiones/estado';
  var HOY = new Date().toISOString().slice(0, 10);
  /* La versión invalida cachés viejas cuando cambia la forma o el origen del
     dato — sin ella, una caché de pruebas se queda mandando todo el día. La v3
     es la que guarda la respuesta entera: una v2 en el navegador tiene el mapa
     de regiones en la raíz y se leería como un payload sin `.regiones`. */
  var LLAVE_ESTADO = 'vault_regiones_v3_' + HOY;

  function estadoCacheado() {
    try {
      var raw = localStorage.getItem(LLAVE_ESTADO);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function guardarEstado(datos) {
    try {
      /* Barre lo de días pasados: sin esto cada día deja su tanda y termina
         llenando la cuota (mismo aprendizaje que vault-referencia.js). */
      for (var i = localStorage.length - 1; i >= 0; i--) {
        var k = localStorage.key(i);
        if (k && k.indexOf('vault_regiones') === 0 && k !== LLAVE_ESTADO) {
          localStorage.removeItem(k);
        }
      }
      localStorage.setItem(LLAVE_ESTADO, JSON.stringify(datos));
    } catch (e) {
      /* Sin caché seguimos, nunca rotos. */
    }
  }

  /* En memoria, para toda la vida de la página: incluye el FALLO. Sin esto,
     cuando el endpoint no responde nunca se guarda caché y cada llamada vuelve
     a pedirlo — con el MutationObserver de abajo eso se vuelve una tormenta de
     peticiones que congela el render. Pasó: tiró la página entera. */
  var estadoEnMemoria;
  var estadoPedido = false;
  var enEspera = [];

  function pedirEstado(cb) {
    if (estadoEnMemoria !== undefined) return cb(estadoEnMemoria);
    if (estadoPedido) return enEspera.push(cb);
    estadoPedido = true;

    var resolver = function (v) {
      estadoEnMemoria = v;
      cb(v);
      while (enEspera.length) enEspera.shift()(v);
    };

    var cache = estadoCacheado();
    if (cache) return resolver(cache);
    try {
      fetch(ESTADO_URL, { credentials: 'omit' })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (d) {
          if (d && d.regiones) {
            guardarEstado(d);
            resolver(d);
          } else resolver(null);
        })
        .catch(function () {
          resolver(null);
        });
    } catch (e) {
      resolver(null);
    }
  }

  /** Umbral por debajo del cual una región "está arrancando" y no "funciona". */
  var CATALOGO_MINIMO = 12;

  function nivelDe(info) {
    if (!info) return null;
    if (!info.hub && !info.nodos) return 3;
    if ((info.catalogo || 0) < CATALOGO_MINIMO) return 2.5;
    return 2;
  }

  /**
   * Pidió una región y Shopify no la aplicó = ahí no hay catálogo etiquetado.
   * Sin decirlo, vería las cartas de todo el país creyendo que son de su
   * estado: una mentira silenciosa, peor que una cuadrícula vacía.
   *
   * QUÉ se dice depende del nivel, porque el pedido es opuesto: en el 2b falta
   * que la gente deposite; en el 3, que exista dónde hacerlo.
   */
  function avisarRegionSinCatalogo(barra, estado, regiones) {
    var contenedor = barra.parentNode;
    if (!contenedor) return;

    var info = regiones ? regiones[estado] : null;
    var nivel = nivelDe(info);
    /* Sin datos del endpoint se degrada al mensaje menos comprometido. */
    var selector = nivel === 3 ? '[data-vault-region-expansion]' : '[data-vault-region-creciendo]';
    var bloque = contenedor.querySelector(selector);

    if (bloque) {
      var nombres = bloque.querySelectorAll('[data-vault-region-nombre]');
      for (var i = 0; i < nombres.length; i++) nombres[i].textContent = estado;
      bloque.hidden = false;
      if (nivel === 3) prepararFormulario(bloque, estado);
    }

    var txt = barra.querySelector('[data-vault-region-nacional-txt] span:last-child');
    if (txt) {
      txt.innerHTML =
        nivel === 3
          ? 'Todavía no llegamos a <strong>' + estado + '</strong>'
          : '<strong>' + estado + '</strong> está arrancando';
    }

    medir(nivel === 3 ? 'region_sin_nodo' : 'region_creciendo', estado);
  }

  /**
   * El filtro opera en TODA la tienda, no solo en las colecciones: si el chip
   * dice Jalisco, el home no puede seguir enseñando carruseles de CDMX.
   *
   * Aquí se filtra en cliente porque el home se renderiza en servidor y se
   * cachea por URL — la región vive en el navegador, así que no hay forma de
   * pedirle a Shopify un home ya filtrado.
   *
   * SALVAGUARDA, y no es menor: si NINGUNA tarjeta de la página trae regiones,
   * el dato todavía no existe (los tags los escribe el backfill de ADR 180, que
   * puede no haber corrido). Filtrar en ese estado escondería el catálogo
   * entero. Ante la duda, no se filtra nada.
   */
  function filtrarTarjetas(estado) {
    var tarjetas = document.querySelectorAll('[data-regiones]');
    if (!tarjetas.length) return;

    var conDato = 0;
    for (var i = 0; i < tarjetas.length; i++) {
      if ((tarjetas[i].getAttribute('data-regiones') || '').trim()) conDato++;
    }
    if (conDato === 0) return; /* el catálogo aún no está etiquetado */

    for (var j = 0; j < tarjetas.length; j++) {
      var suyas = (tarjetas[j].getAttribute('data-regiones') || '').split('|');
      var pasa = !estado || suyas.indexOf(estado) !== -1;
      tarjetas[j].hidden = !pasa;
    }

    ocultarCarruselesVacios();
    avisarListaVacia(estado);
  }

  /**
   * La lista de singles filtrada y en cero. Como el filtro SOLO se ofrece donde
   * hay hub, esto siempre significa "falta inventario" y nunca "no llegamos":
   * el pedido correcto es que alguien suba cartas, no el programa de expansión.
   */
  function avisarListaVacia(estado) {
    var aviso = document.querySelector('[data-vault-region-sin-inventario]');
    if (!aviso) return;

    var grid = document.getElementById('ProductGridContainer');
    if (!grid) return;

    var vivas = grid.querySelectorAll('[data-regiones]:not([hidden])');
    var vacia = !!estado && vivas.length === 0;

    if (vacia) {
      var nombre = aviso.querySelector('[data-vault-region-nombre]');
      if (nombre) nombre.textContent = estado;
      medir('region_lista_vacia', estado);
    }
    aviso.hidden = !vacia;
  }

  /**
   * Un carrusel sin nada que enseñar se quita entero: dejarlo vacío se ve roto,
   * y con el encabezado puesto promete algo que no está.
   */
  function ocultarCarruselesVacios() {
    var cintas = document.querySelectorAll('.section-vault-cinta, .vault-cinta');
    for (var i = 0; i < cintas.length; i++) {
      var vivas = cintas[i].querySelectorAll('[data-regiones]:not([hidden])');
      cintas[i].hidden = vivas.length === 0;
      if (!cintas[i].hidden) adaptarFilas(cintas[i]);
    }
  }

  /**
   * La cinta es una marquesina: duplica el grupo y desplaza -50% en bucle. Ese
   * -50% solo cuadra si los dos grupos miden lo mismo que al renderizar — al
   * esconder cartas encogen, y la animación queda desfasada (se ve el salto y
   * huecos a medio recorrido).
   *
   * Si con lo que queda no se llena el ancho, no tiene sentido desfilar nada:
   * la fila se vuelve estática y centrada, y el grupo duplicado —que es
   * decorativo, va con aria-hidden— se retira para no repetir cartas.
   */
  function adaptarFilas(cinta) {
    var filas = cinta.querySelectorAll('.vault-cinta__fila');
    for (var i = 0; i < filas.length; i++) {
      var grupos = filas[i].querySelectorAll('.vault-cinta__grupo');
      if (!grupos.length) continue;

      var visibles = filas[i].querySelectorAll('[data-regiones]:not([hidden])').length;
      if (visibles === 0) {
        filas[i].hidden = true;
        continue;
      }
      filas[i].hidden = false;

      /* Se mide el PRIMER grupo: el segundo es la copia del bucle. */
      var anchoGrupo = grupos[0].scrollWidth;
      var anchoVisible = cinta.clientWidth || window.innerWidth;
      var llena = anchoGrupo > anchoVisible;

      filas[i].classList.toggle('vault-cinta__fila--estatica', !llena);
      for (var j = 1; j < grupos.length; j++) grupos[j].hidden = !llena;
    }
  }

  /**
   * El chip refleja la decisión del comprador, siempre.
   *
   * No basta con pintarlo desde `current_tags`: si eligió una región sin
   * inventario, Shopify ignora el tag y al recargar el chip volvía a decir
   * "Todo el país" — como si su elección no hubiera ocurrido. Se ve como que
   * el control está roto, cuando lo que pasa es que esa región está vacía.
   *
   * Además marca la opción elegida dentro del panel, para que al reabrirlo se
   * vea dónde está parado.
   */
  function reflejarEleccion(estado) {
    var etiquetas = document.querySelectorAll('[data-vault-region-etiqueta]');
    for (var i = 0; i < etiquetas.length; i++) {
      etiquetas[i].textContent = estado || 'Todo el país';
    }

    var opciones = document.querySelectorAll('.vault-region__opcion');
    for (var j = 0; j < opciones.length; j++) {
      var suyo = opciones[j].getAttribute('data-estado') || '';
      var esNacional = opciones[j].hasAttribute('data-vault-region-nacional');
      var elegida = estado ? suyo === estado : esNacional;
      if (elegida) opciones[j].setAttribute('aria-current', 'true');
      else opciones[j].removeAttribute('aria-current');
    }
  }

  /**
   * Qué región representa el chip AHORA.
   *
   * En una colección manda lo que se ve: la aplicada, o la pedida cuando
   * Shopify la ignoró por no tener catálogo. Fuera de una colección no hay
   * catálogo que contradecir, así que vale su preferencia guardada.
   */
  function regionEfectiva() {
    var barra = document.querySelector('[data-vault-region]');
    var aplicada = barra ? barra.getAttribute('data-region-activa') || '' : '';
    if (aplicada) return aplicada;

    var pedida = regionPedida();
    if (pedida) return pedida;

    var enColeccion = document.querySelector('[data-vault-region-picker][data-en-coleccion="1"]');
    if (enColeccion) return '';

    /* La preferencia guardada solo vale si ese estado SIGUE siendo filtrable:
       si perdió su hub —o si se guardó cuando todavía se ofrecían estados que
       no filtraban nada— dejarla puesta muestra un chip que no corresponde a
       nada. Se limpia y se vuelve a nacional. */
    var recordada = leer();
    if (!recordada) return '';
    var opcion = document.querySelector(
      '.vault-region__opcion[data-estado="' + recordada.replace(/"/g, '') + '"]'
    );
    if (opcion && opcion.hidden) {
      guardar('');
      return '';
    }
    return recordada;
  }

  /**
   * El programa de expansión NO es un modal propio: es "Cómo se abre una
   * ciudad", que el tema ya tenía y explica el modelo mejor que cualquier
   * bloque nuevo (vault-modal-expansion, ADR 153). Ese modal ya prometía que
   * "cada solicitud se suma al conteo de su estado" — y nada las contaba.
   * Aquí solo se prepara su formulario y se le sugiere la región.
   *
   * Abrirlo y cerrarlo es trabajo de `vault-modal.js`: los disparadores llevan
   * `data-vault-modal="expansion"` y un href real a la landing, así que sin JS
   * el clic sigue llevando a algún lado.
   */
  /** Abre "Cómo se abre una ciudad" con el estado ya puesto. */
  function abrirExpansion(estado) {
    var panel = document.querySelector('[data-vault-modal-panel="expansion"]');
    if (!panel || typeof panel.showModal !== 'function') {
      /* Sin `<dialog>` (o sin el panel en esta página) se cae a la landing:
         nunca un clic que no lleva a nada. */
      window.location.href = 'https://cards.the-vault.mx/programa/vault-expansion';
      return;
    }
    prepararFormulario(panel, estado);
    panel.showModal();
    document.documentElement.style.overflow = 'hidden';
    panel.addEventListener('close', function () {
      document.documentElement.style.overflow = '';
    }, { once: true });
    medir('region_sin_nodo_modal', estado);
  }

  function engancharModalExpansion() {
    var panel = document.querySelector('[data-vault-modal-panel="expansion"]');
    if (!panel) return;

    /* Se llena de una vez, sin esperar a que alguien lo abra: el modal también
       lo abren disparadores del tema —el card "Coordina tu región"— que no
       pasan por este código. Antes el select llegaba vacío y no se podía
       elegir estado. */
    prepararFormulario(panel, '');

    document.addEventListener('click', function (e) {
      var disparador = e.target.closest ? e.target.closest('[data-vault-region-aplicar]') : null;
      if (!disparador) return;

      /* Si el aviso ya sabe de qué región hablamos se la pasamos; desde el
         picker no hay región pedida y el formulario la pregunta. */
      var aviso = disparador.closest ? disparador.closest('[data-vault-region-expansion]') : null;
      var nombre = aviso ? aviso.querySelector('[data-vault-region-nombre]') : null;
      prepararFormulario(panel, nombre ? nombre.textContent.trim() : '');
    });
  }

  /**
   * Marca cada estado del panel con su nivel, para que el comprador vea a qué
   * va ANTES de hacer clic. Elegir un estado y descubrir que no hay nada es
   * una decepción evitable, y la lista es justo donde se puede evitar.
   */
  function anotarNiveles(raiz, regiones) {
    if (!regiones) return;
    var opciones = raiz.querySelectorAll('.vault-region__opcion[data-estado]');
    for (var i = 0; i < opciones.length; i++) {
      var estado = opciones[i].getAttribute('data-estado');
      var nivel = nivelDe(regiones[estado]);
      /* Disponible = hay dónde recoger Y hay qué comprar. El conteo exacto no
         va aquí: en una lista de 32 lo que se decide es "¿puedo o no?", y los
         números invitan a comparar regiones, que no es la pregunta. El detalle
         vive en la barra, después de elegir. */
      if (nivel === 2 || nivel === 2.5) {
        opciones[i].setAttribute('data-nivel', 'disponible');
        opciones[i].hidden = false;
      } else {
        /* Sin hub —o sin dato, que para el comprador es lo mismo— no se ofrece
           como filtro: elegirlo no cambiaba nada y se sentía roto. Quien no
           encuentre el suyo tiene el "aplica aquí" del pie, que es la salida
           honesta.

           El `else` cubre también `nivel === null` (el estado no vino en la
           respuesta). Antes solo se ocultaba el nivel 3 y los desconocidos se
           quedaban visibles: clicables, y sin que pasara nada. */
        opciones[i].setAttribute('data-nivel', 'proximamente');
        opciones[i].hidden = true;
      }
    }
  }

  /**
   * Nivel 3: captura la demanda que hoy se pierde.
   *
   * `estado` puede venir vacío: cuando se llega por "aplica aquí" desde el
   * picker no hubo región pedida, así que se pregunta en el propio formulario.
   */
  function prepararFormulario(bloque, estado) {
    var form = bloque.querySelector('[data-vault-region-form]');
    if (!form) return;

    var selectEstado = form.querySelector('[data-vault-region-estado-select]');

    /* La sugerencia se actualiza en CADA apertura: el modal es uno solo y se
       puede abrir desde varios lados, cada uno sabiendo una región distinta. */
    if (selectEstado && selectEstado.options.length > 1) {
      var previa = estado || leer();
      if (previa) selectEstado.value = previa;
    }

    /* Lo de abajo se arma una sola vez. */
    if (form.dataset.listo) return;
    form.dataset.listo = '1';

    if (selectEstado) {
      /* Se llena de las opciones del picker: una sola lista de estados en toda
         la página, sin una segunda copia que se desincronice. */
      var fuente = document.querySelectorAll('.vault-region__opcion[data-estado]');
      var vistos = {};
      var html = '<option value="">Elige tu estado…</option>';
      for (var k = 0; k < fuente.length; k++) {
        var e = fuente[k].getAttribute('data-estado');
        if (!e || vistos[e]) continue;
        vistos[e] = 1;
        html += '<option value="' + e + '">' + e + '</option>';
      }
      selectEstado.innerHTML = html;
      /* Se preselecciona lo que sepamos —la región del aviso, o la recordada—
         pero se deja editable: puede estar preguntando por otra ciudad. */
      var sugerida = estado || leer();
      if (sugerida) selectEstado.value = sugerida;
    }

    var msg = bloque.querySelector('[data-vault-region-msg]');
    function decir(texto, ok) {
      if (!msg) return;
      msg.textContent = texto;
      msg.hidden = false;
      msg.setAttribute('data-ok', ok ? '1' : '0');
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var estadoFinal = (selectEstado && selectEstado.value) || estado || '';
      if (!estadoFinal) {
        decir('Elige tu estado.', false);
        return;
      }
      var email = (form.elements.email.value || '').trim();
      var tienda = (form.elements.tienda.value || '').trim();
      if (!email && !tienda) {
        decir('Déjanos tu correo o el nombre de una tienda.', false);
        return;
      }

      var boton = form.querySelector('button[type="submit"]');
      if (boton) boton.disabled = true;

      fetch('https://cards.the-vault.mx/api/regiones/interes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estado: estadoFinal,
          email: email,
          tienda: tienda,
          website: form.elements.website.value || '',
        }),
      })
        .then(function (r) {
          return r.json().catch(function () {
            return {};
          });
        })
        .then(function (d) {
          if (d && d.ok) {
            decir('Listo, quedamos atentos. Gracias por empujar tu ciudad.', true);
            form.querySelector('.vault-region__campos').hidden = true;
            if (boton) boton.hidden = true;
            medir('region_interes', estadoFinal);
          } else {
            decir((d && d.error) || 'No se pudo guardar. Intenta de nuevo.', false);
            if (boton) boton.disabled = false;
          }
        })
        .catch(function () {
          decir('No se pudo guardar. Intenta de nuevo.', false);
          if (boton) boton.disabled = false;
        });
    });
  }

  /**
   * El CONTROL. Vive en el header (global) y también, como atajo, en la barra
   * de la colección. Puede haber más de uno en la página: cada uno se inicia
   * solo y todos comparten memoria y niveles.
   */
  function iniciarPicker(picker, regiones) {
    if (picker.dataset.listo) return;
    picker.dataset.listo = '1';

    anotarNiveles(picker, regiones);

    /* Las opciones son enlaces: navega el navegador. Aquí solo se recuerda y
       se mide. */
    var opciones = picker.querySelectorAll('.vault-region__opcion[data-estado]');
    var recordada = leer();
    for (var i = 0; i < opciones.length; i++) {
      if (recordada && opciones[i].getAttribute('data-estado') === recordada) {
        opciones[i].setAttribute('data-recordado', '');
      }
      opciones[i].addEventListener('click', function (ev) {
        var estado = this.getAttribute('data-estado') || '';
        guardar(estado);
        /* ADR 189 §4 — la intención se captura AQUÍ, antes de navegar: si esa
           región no tiene un solo producto etiquetado, Shopify contesta 301 y
           la URL de destino ya no la menciona. */
        recordarPedida(estado);
        marcarDestino(this, estado);
        medir('region_filter_on', estado);

        /* Estado sin nodo: no se navega a ningún lado. Llevarlo a la colección
           solo para enseñarle el catálogo NACIONAL y un aviso de que ahí no
           llegamos es dar un rodeo para decir que no. Se le abre el programa de
           expansión encima, con su estado ya puesto — que es justo lo que puede
           hacer al respecto, y en el momento en que le importa. */
        if (this.getAttribute('data-nivel') === 'proximamente') {
          ev.preventDefault();
          picker.open = false;
          reflejarEleccion(estado);
          abrirExpansion(estado);
          return;
        }

        /* Es un filtro GLOBAL: cambiar de región no debe sacarte de donde
           estás. En una colección se re-filtra esa misma colección (te quedas
           en ella); en el home o una ficha solo se recuerda la preferencia y
           se actualiza el chip, sin navegar a ningún lado. */
        if (picker.getAttribute('data-en-coleccion') !== '1') {
          ev.preventDefault();
          reflejarEleccion(estado);
          picker.open = false;
        }
      });
    }

    /* Volver a "todo el país" es quitar un filtro, no ir a otra parte. Es un
       control GLOBAL: se apaga donde estés, sea el home, una ficha o una
       colección. Antes esto vivía en iniciarBarra, que solo corre si hay barra
       de colección — así que en el home el enlace navegaba y te mandaba a la
       lista de singles. */
    var salidas = picker.querySelectorAll('[data-vault-region-nacional]');
    for (var n = 0; n < salidas.length; n++) {
      salidas[n].addEventListener('click', function (ev) {
        medir('region_filter_off', leer());

        /* ÚNICA excepción: si Shopify ya filtró en servidor (la URL trae el
           tag), las otras cartas no están en el DOM y no hay nada que volver a
           mostrar — hay que pedir la colección sin el tag. Ahí el enlace hace
           su trabajo. En cualquier otra pantalla se apaga sin moverse. */
        var barra = document.querySelector('[data-vault-region]');
        var filtradoEnServidor = barra && barra.getAttribute('data-region-activa');
        guardar('');
        /* Apagar el filtro también borra la intención: si no, el aviso de
           "todavía no llegamos a X" reaparecería en la siguiente pantalla de
           esta misma pestaña, contestando una pregunta que ya se retiró. */
        recordarPedida('');
        if (filtradoEnServidor) return;

        ev.preventDefault();
        /* Y se limpia de la URL, que es la otra copia de la intención: sin esto
           un F5 volvería a encender el aviso recién apagado. */
        limpiarQuery();
        reflejarEleccion('');
        filtrarTarjetas('');
        picker.open = false;
      });
    }

    /* `details` no se cierra al hacer clic fuera; sin esto el panel se queda
       abierto tapando la página. */
    document.addEventListener('click', function (e) {
      if (picker.open && !picker.contains(e.target)) picker.open = false;
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && picker.open) picker.open = false;
    });
  }

  /** La CONSECUENCIA: qué pasó con la región donde estás parado. */
  function iniciarBarra(barra, regiones) {
    if (barra.dataset.listo) return;
    barra.dataset.listo = '1';

    var activa = barra.dataset.regionActiva || '';
    var pedida = regionPedida();

    /* La región de la URL manda sobre la recordada: si llegó por un enlace a
       /region-jalisco, esa es su intención de ahora. */
    if (activa || pedida) guardar(activa || pedida);

    if (!activa && pedida) avisarRegionSinCatalogo(barra, pedida, regiones);

    /* Las salidas a nacional se enganchan en iniciarPicker: viven en el header
       y en los avisos, no solo aquí, y esta función solo corre si hay barra. */
  }

  /**
   * Lo que este módulo le presta al resto del tema (ADR 189 §5/§6).
   *
   * `datos` entrega la respuesta completa de `/api/regiones/estado` con la
   * caché y la protección contra tormentas ya puestas — quien lo use no vuelve
   * a pedir el endpoint. `elegida` es la región del comprador, y es la ÚNICA
   * fuente que el storefront tiene permitido usar: nunca por IP (ADR 189 §6).
   */
  window.vaultRegion = {
    datos: pedirEstado,
    elegida: leer,
  };

  function arrancar() {
    /* Un solo fetch por carga: el nivel de las regiones es el mismo para el
       picker del header y para la barra de la colección. */
    pedirEstado(function (payload) {
      var regiones = payload ? payload.regiones : null;
      var pickers = document.querySelectorAll('[data-vault-region-picker]');
      for (var i = 0; i < pickers.length; i++) iniciarPicker(pickers[i], regiones);

      var barras = document.querySelectorAll('[data-vault-region]');
      for (var j = 0; j < barras.length; j++) iniciarBarra(barras[j], regiones);

      engancharModalExpansion();
      var efectiva = regionEfectiva();
      reflejarEleccion(efectiva);
      filtrarTarjetas(efectiva);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }

  /* El grid se re-renderiza al ordenar o paginar (facets.js reemplaza la
     sección), así que la barra puede volver a nacer sin recargar la página. */
  /* El grid se re-renderiza al ordenar o paginar (facets.js reemplaza la
     sección), así que la barra puede volver a nacer sin recargar. El observer
     solo reacciona si aparece un nodo NUESTRO sin iniciar, y se agenda una vez
     por tick: reaccionar a cualquier nodo agregado —imágenes perezosas, apps de
     terceros— hacía que se re-ejecutara sin parar. */
  if (window.MutationObserver) {
    var agendado = false;
    new MutationObserver(function () {
      if (agendado) return;
      var pendiente = document.querySelector(
        '[data-vault-region-picker]:not([data-listo]), [data-vault-region]:not([data-listo])'
      );
      if (!pendiente) return;
      agendado = true;
      setTimeout(function () {
        agendado = false;
        arrancar();
      }, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
